import { FC } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { IQueryParamDrivenDrawerContentProvider } from '../components/query-param-driver-drawer/IQueryParamDrivenDrawerContentProvider';
import { QueryParamDrivenDrawerHelper } from '../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { QueryParamDrivenDrawerPage } from '../components/CareMapDrawerPage';
import { BlockUntilActiveCaremap } from '../ActiveCareMapProvider';
import { MemoryScope } from './MemoriesModel';
import { MemoryDetailPanel } from './MemoryDetailPanel';

interface IViewMemoryDrawerProps {
    memoryId: string;
    scope: MemoryScope;
}

export class ViewMemoryWithQueryParamDrawer implements IQueryParamDrivenDrawerContentProvider<IViewMemoryDrawerProps> {
    static inject = () => [QueryParamDrivenDrawerHelper];
    constructor(private drawerHelper: QueryParamDrivenDrawerHelper<IViewMemoryDrawerProps>){}

    mode = 'view-memory';

    Component = ViewMemoryDrawerView;

    options = {
        allowBackdropClose: true
    };

    open = (props: IViewMemoryDrawerProps) => {
        this.drawerHelper.open({ mode: this.mode, ...props });
    };

    // Repoints an already-open drawer's URL at a memory's new identity after an edit supersedes
    // it (see MemoryDetailPanel's onEdited) -- a REPLACE so the dead memoryId never becomes a
    // back-button stop the user could land back on.
    replace = (props: IViewMemoryDrawerProps) => {
        this.drawerHelper.replace({ mode: this.mode, ...props });
    };

    getUrl = (props: IViewMemoryDrawerProps) => {
        return this.drawerHelper.getUrl({ mode: this.mode, ...props });
    };
};

const ViewMemoryDrawerContent: FC<IViewMemoryDrawerProps> = ({ memoryId, scope }) => {
    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);
    const viewMemoryDrawer = useInstance(ViewMemoryWithQueryParamDrawer);

    // Repoint the ?drawer= URL (via history REPLACE, not push) after a successful edit so a reload
    // still resolves to the live row instead of the dead id baked into the query param.
    return (
        <MemoryDetailPanel
            memoryId={memoryId}
            scope={scope}
            onClose={drawerHelper.close}
            onSupersededByEdit={newMemoryId => viewMemoryDrawer.replace({ memoryId: newMemoryId, scope })}
        />
    );
};

export const ViewMemoryDrawerView: FC<IViewMemoryDrawerProps> = () => {
    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);
    const { memoryId, scope } = drawerHelper.getDrawerParams();

    const content = <ViewMemoryDrawerContent memoryId={memoryId} scope={scope} />;

    return (
        <QueryParamDrivenDrawerPage
            title="What Bea Remembers"
            hasBack={false}
            data-type="view-memory-drawer"
            onClose={drawerHelper.close}
        >
            {scope === 'care-recipient' ? (
                <BlockUntilActiveCaremap>{content}</BlockUntilActiveCaremap>
            ) : content}
        </QueryParamDrivenDrawerPage>
    );
};
